import type { GridCodec } from "./GridCodec";
import type { GridModel } from "./GridModel";

/**
 * A server grid that does not match the title's grid: the wrong number of rows, the wrong number of cells in a row,
 * or an empty cell. It surfaces through the presentation failure path (decision 17): the coordinator's generic
 * reload, `playback_failed` in a replay, `restore_failed` in a restore.
 */
export class GridDecodeError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "GridDecodeError";
  }
}

/**
 * The platform's grid codec: '|' between rows, ',' between cells, row-major — the format the shell's
 * `sim/fixtures/dsl/GridEncoding.ts` and the backend's game-sdk write. Strict: a snapshot that is not exactly
 * `columnCount` × `rowCount` non-empty ids throws `GridDecodeError`, so a string without the separators fails on any
 * grid wider or taller than one. Ids are trimmed; nothing is padded or invented. Until decision 40 slot-cannon decoded
 * with a lenient `DummyGridCodec` that padded a short grid with "?" and turned a string without both separators into
 * placeholder ids.
 */
export class DelimitedGridCodec implements GridCodec {
  private readonly columnCount: number;
  private readonly rowCount: number;

  public constructor(columnCount: number, rowCount: number) {
    this.columnCount = columnCount;
    this.rowCount = rowCount;
  }

  public decode(encodedGridSnapshot: string): GridModel {
    const rows = encodedGridSnapshot.split("|");
    if (rows.length !== this.rowCount) {
      throw new GridDecodeError(`DelimitedGridCodec: expected ${this.rowCount} rows, got ${rows.length} in ${JSON.stringify(encodedGridSnapshot)}`);
    }
    const cells = rows.map((row, r) => {
      const ids = row.split(",");
      if (ids.length !== this.columnCount) {
        throw new GridDecodeError(`DelimitedGridCodec: expected ${this.columnCount} cells in row ${r}, got ${ids.length} in ${JSON.stringify(encodedGridSnapshot)}`);
      }
      return ids.map((id, c) => {
        const symbolId = id.trim();
        if (symbolId === "") throw new GridDecodeError(`DelimitedGridCodec: empty cell at row ${r}, column ${c} in ${JSON.stringify(encodedGridSnapshot)}`);
        return { symbolId };
      });
    });
    return { columnCount: this.columnCount, rowCount: this.rowCount, cells };
  }
}
